/**
 * sl-k78j.6.1: Workspace Pricing Plans Tab
 *
 * Displays pricing plan comparison and upgrade options within the
 * workspace settings. Uses the shared PricingCards component.
 *
 * sl-fwcn: Addon intervals follow plan interval automatically — no opt-in dialog.
 */

import { SettingHeader } from '@affine/component/setting-components';
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import {
  clearWorkspacePlansSubscribeParams,
  getWorkspacePlansSubscribeIntent,
} from '@affine/core/components/pricing/subscribe-deeplink';
import {
  SeatAddonService,
  WorkspaceSubscriptionService,
} from '@affine/core/modules/cloud';
import {
  isDisplayableBillingSubscription,
  isTrialingSubscriptionCurrent,
} from '@affine/core/modules/cloud/utils/subscription-state';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { Fragment, Suspense, useCallback, useEffect, useMemo } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { useSearchParams } from 'react-router-dom';

import { PricingCards } from '../../../../../components/pricing/pricing-cards';
import * as styles from '../../../../../components/pricing/pricing-cards.css';
import { SWRErrorBoundary } from '../../../../../components/pure/swr-error-bundary';
import {
  SettingsOfflineHeaderAction,
  useSettingsOfflineIncompleteLabel,
} from '../../shared/settings-offline-status';
import { CreditPurchaseSection } from '../ai/credit-purchase-dialog';
import { useWorkspaceCreditSubscriptions } from '../ai/use-managed-ai';
import { SeatAddonSection } from '../billing/seat-addon-section';

const TRUST_ITEMS = [
  'com.streamlain.payment.platform.one-workspace',
  'com.streamlain.payment.platform.no-shadow-ai',
  'com.streamlain.payment.platform.eu-data',
  'com.streamlain.payment.platform.cost-control',
] as const;

function PlatformTrustStrip() {
  const t = useI18n();
  return (
    <div className={styles.platformTrustStrip}>
      {TRUST_ITEMS.map((key, i) => (
        <Fragment key={key}>
          {i > 0 && <span className={styles.platformTrustDot}>·</span>}
          <span className={styles.platformTrustItem}>{t[key]()}</span>
        </Fragment>
      ))}
    </div>
  );
}

const PlansErrorBoundary = ({ resetErrorBoundary }: FallbackProps) => {
  const t = useI18n();

  return (
    <div style={{ padding: '24px', textAlign: 'center' }}>
      <p style={{ color: 'var(--streamlain-text-secondary-color, #6b7280)' }}>
        {t['com.streamlain.payment.plans-error-tip']()}
      </p>
      <a
        onClick={resetErrorBoundary}
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          color: 'var(--streamlain-primary-color, #1e96eb)',
        }}
      >
        {t['com.streamlain.payment.plans-error-retry']()}
      </a>
    </div>
  );
};

const WorkspaceSettingPlansContent = () => {
  const t = useI18n();
  const backendAvailable = useBackendAvailable();
  const offlineLabel = useSettingsOfflineIncompleteLabel();
  const wsSubService = useService(WorkspaceSubscriptionService);
  const seatAddonService = useService(SeatAddonService);
  const workspace = useService(WorkspaceService).workspace;
  const wsSub = useLiveData(wsSubService.subscription.subscription$);
  const billingState = useLiveData(wsSubService.subscription.billingState$);
  const addon = useLiveData(seatAddonService.seatAddon.addon$);
  const hasExtraSeats = useLiveData(seatAddonService.seatAddon.hasExtraSeats$);
  const { subscriptions: creditSubs } = useWorkspaceCreditSubscriptions(
    workspace.id,
    workspace.flavour
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const subscribeIntent = useMemo(
    () => getWorkspacePlansSubscribeIntent(searchParams),
    [searchParams]
  );

  // Resolved plan from feature gate — single source of truth (handles planOverride).
  const { plan: resolvedPlan } = useFeatureGate();

  useEffect(() => {
    wsSubService.subscription.revalidate();
  }, [wsSubService]);

  const activePlanName = billingState?.effectivePlan ?? resolvedPlan;
  const rawBillingSubscription = billingState?.billingSubscription ?? wsSub;
  const billingSubscription = isDisplayableBillingSubscription(
    rawBillingSubscription
  )
    ? rawBillingSubscription
    : null;
  const billingPlanName = billingSubscription?.plan ?? null;
  const currentPlanName =
    billingPlanName ?? (billingState?.adminOverride ? 'Free' : activePlanName);
  const overridePlanName = billingState?.adminOverride?.plan ?? null;
  const currentRecurring = billingSubscription?.recurring ?? null;

  // Trial subscriptions have no payment method — addon purchases would fail.
  // Hide addon sections until the user upgrades to a paid subscription.
  const isTrialing = isTrialingSubscriptionCurrent(billingSubscription);

  // sl-fwcn.5.2: §312j BGB — calculate active addon costs for consent dialog
  const activeAddonCosts = useMemo(() => {
    const costs: Array<{ label: string; amountCents: number }> = [];
    if (hasExtraSeats && addon) {
      costs.push({
        label: `${addon.quantity} Extra Seats`,
        amountCents: addon.totalYearly ?? 0,
      });
    }
    // Credit subscriptions: include as line items (amount 0 since yearly price
    // is not on the GraphQL type — the total cost line will reflect seats only,
    // but the user sees credit costs are included in the cascade)
    for (const sub of creditSubs) {
      if (!sub.cancelAtPeriodEnd) {
        costs.push({
          label: `${sub.credits} Credits/mo`,
          amountCents: 0,
        });
      }
    }
    return costs;
  }, [hasExtraSeats, addon, creditSubs]);

  const clearSubscribeIntent = useCallback(() => {
    setSearchParams(clearWorkspacePlansSubscribeParams(searchParams), {
      replace: true,
    });
  }, [searchParams, setSearchParams]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SettingHeader
        title={t['com.streamlain.payment.title']()}
        action={<SettingsOfflineHeaderAction show={!backendAvailable} />}
      />
      {!backendAvailable ? <p>{offlineLabel}</p> : null}
      <PlatformTrustStrip />
      <PricingCards
        activePlanName={activePlanName}
        currentPlanName={currentPlanName}
        overridePlanName={overridePlanName}
        billingPlanName={billingPlanName}
        currentRecurring={currentRecurring}
        subscriptionEnd={wsSub?.end ?? null}
        activeAddonCosts={activeAddonCosts}
        isTrialing={isTrialing}
        autoCheckout={subscribeIntent}
        checkoutCoupon={subscribeIntent?.coupon ?? null}
        onAutoCheckoutHandled={clearSubscribeIntent}
      />
      {!isTrialing && (
        <Suspense fallback={null}>
          <SeatAddonSection />
        </Suspense>
      )}
      {!isTrialing && (
        <Suspense fallback={null}>
          <CreditPurchaseSection />
        </Suspense>
      )}
    </div>
  );
};

export const WorkspaceSettingPlans = () => {
  return (
    <SWRErrorBoundary FallbackComponent={PlansErrorBoundary}>
      <WorkspaceSettingPlansContent />
    </SWRErrorBoundary>
  );
};
